import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormInput from "../FormInput/FormInput";
import FormTextarea from "../FormTextarea/FormTextarea";
import { CircleIcon } from "../../assets/icon";

import s from "./FormConfiguration.module.scss";
import {
  DEFAULT_BOAT_SKU,
  FORM_STATUS,
  orgId,
  restApi,
} from "../../utils/constants";
import { createSnapshot } from '../../utils/playcanvasChange';
import { useParams } from "react-router";
import { assetsMap } from "../../utils/assetsMap";
import { useSelector, useDispatch } from "react-redux";
import {
  getActiveParams,
  getActiveColorInGroup,
  getParamsSaveConfiguration,
} from "../../redux/selectors";

import { getIpAddress, SavedConfiguration, sendFormToRest } from "../../api";
import FormCheckBox from "../FormCheckBox/FormCheckBox";
import { changeModalState } from "../../redux/actions/Modals";
import FormSelect from "../FormSelect/FormSelect";

export const FormConfiguration = () => {
  let { id } = useParams();
  const dispatch = useDispatch();

  let activeParams = useSelector(getActiveParams);
  const infoActiveElement = useSelector(getActiveColorInGroup);
  const paramsSaveConfiguration = useSelector(getParamsSaveConfiguration);

  const sku = id || DEFAULT_BOAT_SKU;

  const boat = !!assetsMap[sku]
    ? assetsMap[sku]
    : assetsMap[DEFAULT_BOAT_SKU].name;

  const labelName = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone Number",
    address: "Address",
    streetAddress: "Street Address",
    addressLine: "Address Line 2",
    city: "City",
    zip: "Zip Code",
    country: "Country",
  };

  const nameErrors = {
    firstName: {
      empty: "First Name cannot be empty",
    },
    lastName: {
      empty: "Last Name cannot be empty",
    },
    email: {
      invalid: "email is invalid",
      empty: "email cannot be empty",
    },
    phone: {
      empty: "phone cannot be empty",
    },
  };

  const schema = yup.object().shape({
    firstName: yup.string().required(nameErrors.firstName.empty),
    lastName: yup.string().required(nameErrors.lastName.empty),
    email: yup
      .string()
      .email(nameErrors.email.invalid)
      .required(nameErrors.email.empty),
    phone: yup.string().required(nameErrors.phone.empty),
  });

  const { register, handleSubmit, errors, reset } = useForm({
    resolver: yupResolver(schema),
  });

  const [submittedData, setSubmittedData] = React.useState({});
  const [formStatus, setFormStatus] = useState(FORM_STATUS.NOT_SUBMITTED);

  const onSubmit = async (data) => {
    const {
      email,
      firstName,
      lastName,
      phone,
      streetAddress,
      zip,
      CommunicationOptIn,
      country,
      city,
      region,
    } = data;

    try {
      const ip = await getIpAddress();
      const countryCode = JSON.parse(country).code
      const snapshot = await createSnapshot();

      setFormStatus(FORM_STATUS.PROCESS);

      const saveConfig = await SavedConfiguration({
        assetId: infoActiveElement.assetId,
        orgId: orgId,
        metadata: { ...paramsSaveConfiguration }
      })

      const link = `${window.location.href}?sku=${saveConfig['shortId']}`;


      const requestBody = {
        crm: [
          {
            Brands: "Premier",
            LeadSourceName: "Organic",
            LeadTypeName: "Build a Boat",
            LeadCategoryName: "pontoons.com",
            FirstName: firstName,
            LastName: lastName,
            Address1: streetAddress,
            City: city,
            State: null,
            PostalCode: zip,
            WorkPhone: phone,
            Email: email,
            CountryCode: countryCode,
            IsCommunicationOptIn: CommunicationOptIn,
            CommunicationOptInDate: new Date(),
            CommunicationOptInSource: countryCode !== "US" ? "pontoons.com" : null,
            CommunicationOptInIpAddress: countryCode !== "US" ? ip : null,
            SendAutoresponder: true,
            SendNurture: false,
            LeadReroute: false,
            Products: [{ ProductCode: boat.code || '', ProductModelYear: "2022" }],
            Customs: countryCode === "US" ? [{
              BuildaBoatSummaryURL: link,
              AdditionalComments: JSON.stringify({ModelDescription: boat.name, params: activeParams})
            }] : null,
            Dealers: [],
            SessionUid: window.sessionUid,
            UserUid: window.userUid,
          },
        ],
        configuration: { name: boat.name, params: activeParams, snapshot },
      };

      const pdfName = await sendFormToRest(requestBody);
      setFormStatus(FORM_STATUS.NOT_SUBMITTED);
      const modalData = {link, pdfName}
      dispatch(changeModalState({ modalName: "SaveModal", data: modalData }));

      // const url = `${window.location.origin}/request`;
      // data.sku = sku;
      // data.your_boat = boatName;
      // data.activeParams = JSON.stringify(activeParams);

      // setFormStatus(FORM_STATUS.PROCESS);
      // await fetch(url, {
      //   method: "POST",
      //   headers: {
      //     Accept: "application/json",
      //     "Content-Type": "application/json",
      //   },
      //   body: JSON.stringify(data),
      // }).then((res) => {
      //   if (res.status === 200) {
      //     setSubmittedData(data);
      //     setFormStatus(FORM_STATUS.SUBMITTED);
      //     setTimeout(() => resetForm(), 3000);
      //   }
      // });
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    reset({ ...submittedData });
    setFormStatus(FORM_STATUS.NOT_SUBMITTED);
  };

  const renderCheckIcon = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="10"
        viewBox="0 0 13 10"
        fill="none"
      >
        <path d="M1 3.33333L5.5 8L12 1" stroke="white" strokeWidth="2" />
      </svg>
    );
  };

  const notSubmittedButton = () => {
    return (
      <button type="submit" className={`${s.btn}`}>
        Submit
      </button>
    );
  };

  const processButton = () => {
    return (
      <button type="submit" className={`${s.btn} ${s.btn_process}`}>
        <CircleIcon />
        Submitting
      </button>
    );
  };

  const submittedButton = () => {
    return (
      <button type="submit" className={`${s.btn} ${s.btn_green}`}>
        Message sent
        {renderCheckIcon()}
      </button>
    );
  };

  const renderSubmitButton = () => {
    switch (formStatus) {
      case FORM_STATUS.PROCESS:
        return processButton();
      case FORM_STATUS.SUBMITTED:
        return submittedButton();
      default:
        return notSubmittedButton();
    }
  };

  return (
    <>
      <div className={s.form_title}>get a quote</div>
      <div className={s.form_subtitle}>
        Please fill out all information below and one of our representatives
        will follow up with you.
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
        {/* <div className={s.hint_input}>
          <div className={s.title}>Your Boat</div>
          <div className={s.box}>
            <div className={s.name}>{boatName}</div>
          </div>
        </div> */}

        <FormInput
          required
          errors={errors}
          name="firstName"
          label={labelName.firstName}
          register={register}
        />
        <FormInput
          required
          errors={errors}
          name="lastName"
          label={labelName.lastName}
          register={register}
        />
        <FormInput
          required
          errors={errors}
          name="email"
          label={labelName.email}
          register={register}
        />
        <FormInput
          required
          errors={errors}
          name="phone"
          label={labelName.phone}
          register={register}
        />

        <FormInput
          name="streetAddress"
          label={labelName.streetAddress}
          register={register()}
        />
        <div className={s.form_col_2}>
          <FormInput name="zip" label={labelName.zip} placeholder={"US or Canadian format"} register={register()} />
          <FormSelect
            name="country"
            label={labelName.country}
            register={register}
          />
        </div>

        <FormCheckBox
          name="CommunicationOptIn"
          label="I agree to receive news and promotional emails about Premier products and services"
          register={register()}
        />

        {/* <div className={s.form_group}>
          <div className={s.form_group_label}>Address</div>
          
          <FormInput
            name="addressLine"
            subLabelButton={labelName.addressLine}
            register={register()}
          />
          <div className={s.form_col_2}>
            <FormInput
              name="city"
              subLabelButton={labelName.city}
              register={register()}
            />
            <FormInput
              name="region"
              subLabelButton={labelName.region}
              register={register()}
            />
          </div>
          <div className={s.form_col_2}>
            <FormInput
              name="postal"
              subLabelButton={labelName.postal}
              register={register()}
            />
            <FormInput
              name="country"
              subLabelButton={labelName.country}
              register={register()}
            />
          </div>
        </div> */}
        {/* <FormTextarea
          name="comments"
          label={labelName.comments}
          register={register()}
        /> */}
        {renderSubmitButton()}
      </form>
    </>
  );
};
